import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { WarningMessageBox } from '@xing-com/crate-entity-pages-common';

import { AboutUsSubpageMediaDocument } from '../../graphql/queries/about-us-subpage-media.gql-types';

import { Gallery } from './gallery';
import { AboutUsSkeleton } from './skeleton/skeleton';

const NUMBER_MEDIA_ITEMS = 100;

type GalleryContainerProps = {
  pageSlug: string;
  itemClicked?: number;
  onError?: (error: Error) => void;
};

const GalleryContainerInner: FC<GalleryContainerProps> = ({
  pageSlug,
  itemClicked,
}) => {
  const { data, refetch, error } = useSuspenseQuery(
    AboutUsSubpageMediaDocument,
    {
      variables: {
        id: pageSlug,
        first: NUMBER_MEDIA_ITEMS,
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  );

  const entityPage =
    data?.pagesAboutUs?.__typename === 'AboutEntity'
      ? data.pagesAboutUs
      : undefined;

  const entityMedia =
    entityPage?.media?.__typename === 'EntityMediaConnection'
      ? entityPage.media
      : undefined;

  const mediaEdges = entityMedia?.edges ?? [];

  const mediaNodes = mediaEdges
    .map((edge) => edge?.node)
    .filter((node) => !!node);

  return (
    <>
      {error ? (
        <ErrorFallback onReload={() => refetch()} />
      ) : (
        <Gallery itemClicked={itemClicked} galleryItems={mediaNodes} />
      )}
    </>
  );
};

const ErrorFallback: FC<{ onReload: () => void }> = ({ onReload }) => {
  return (
    <WarningMessageBox
      headerText="EP_ERROR_HEADER"
      bodyText="EP_ERROR_BODY"
      buttonText="EP_ERROR_RELOAD_CTA"
      onClick={onReload}
    />
  );
};

export const GalleryContainer: FC<GalleryContainerProps> = ({
  onError = () => undefined,
  ...props
}) => {
  return (
    <ErrorBoundary
      onError={(error) => {
        onError(error);
        return <ErrorFallback onReload={() => window.location.reload()} />;
      }}
    >
      <Suspense fallback={<AboutUsSkeleton />}>
        <GalleryContainerInner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
